import { gql } from '@apollo/client';
import type {
    FutureStockQuantities,
    GeneralNextAvailabilityStatus,
    ProductOrderRestriction,
    StockQuantity,
} from '../types';
import useBatchedQuery from '../../core/hooks/apollo/useBatchedQuery';
import useLoginState from '../../core/hooks/useLoginState';
import useStoreAndLanguageParams from '../../core/hooks/useStoreAndLanguageParams';
import useCurrentCustomer from '../../core/hooks/useCurrentCustomer';

export interface ProductStockInformationQueryResult {
    singleStockQuantity: StockQuantity;
    productFutureStock: FutureStockQuantities;
    productOrderRestriction?: ProductOrderRestriction;
}

export interface ProductListStockInformationQueryResult {
    stockQuantities: StockQuantity[];
    productListFutureStock: FutureStockQuantities[];
    productListOrderRestriction: ProductOrderRestriction[];
}

const batchQuery = gql`
    query productBulkAvailability(
        $store: ID!
        $skuList: [String!]!
        $location: String!
    ) {
        stockQuantities(store: $store, skuList: $skuList, location: $location) {
            store
            sku
            location
            quantity
            nextAvailability
        }
        productListFutureStock(
            store: $store
            skuList: $skuList
            location: $location
        ) {
            store
            sku
            location
            quantities {
                quantity
                date
            }
        }
        productListOrderRestriction(store: $store, skuList: $skuList) {
            store
            sku
            restrictionId
        }
    }
`;

const query = gql`
    query productBulkAvailabilityItem(
        $store: ID!
        $sku: String!
        $location: String!
    ) {
        singleStockQuantity(store: $store, sku: $sku, location: $location) {
            store
            sku
            location
            quantity
            nextAvailability
        }
        productFutureStock(store: $store, sku: $sku, location: $location) {
            store
            sku
            location
            quantities {
                quantity
                date
            }
        }
        productOrderRestriction(store: $store, sku: $sku) {
            store
            sku
            restrictionId
        }
    }
`;

const useProductAvailability = (skuList: string[], locationParam?: string) => {
    const { store } = useStoreAndLanguageParams();
    const customer = useCurrentCustomer();
    const location = locationParam || customer?.locationCode;
    const isLoggedIn = useLoginState();

    const {
        data: quantities,
        error,
        loading,
    } = useBatchedQuery<
        ProductStockInformationQueryResult,
        ProductListStockInformationQueryResult
    >({
        items: skuList,
        variables: {
            store,
            location,
        },
        options: {
            skip: !isLoggedIn || !location,
        },
        query,
        batchQuery,
        itemParameterName: 'sku',
        itemListParameterName: 'skuList',
        batchToResult: (batch, ids) => {
            const result: ProductStockInformationQueryResult[] = ids.map(
                sku => {
                    return {
                        singleStockQuantity: batch.stockQuantities.find(
                            item => item.sku === sku
                        ) ?? {
                            store,
                            sku: sku as string,
                            location: location,
                            quantity: undefined,
                            nextAvailability:
                                'unknown' as GeneralNextAvailabilityStatus,
                            unavailable: true,
                        },
                        productFutureStock: batch.productListFutureStock.find(
                            item => item.sku === sku
                        ) ?? {
                            store,
                            location: location || '',
                            sku: sku as string,
                            quantities: [],
                        },
                        productOrderRestriction:
                            batch.productListOrderRestriction.find(
                                item => item.sku === sku
                            ),
                    };
                }
            );

            return result;
        },
    });

    return {
        stock: quantities,
        error: error,
        loading: loading,
    };
};

export default useProductAvailability;
